// money.ts — GOLD, the platform's cash currency. $1 = 1 GOLD.
//
// On the wire every monetary value is an int64 *minor unit* at
// MINOR_PRECISION = 4 — a "dollar-major" value where 10000 = $1.00. One
// displayed whole unit is one dollar, so "737500" reads 73.75 and display
// truncates to 2 dp.
//
// **This module used to draw GOLD on the legacy SPARK lens** — 1 SPARK = 1
// cent, so wire 737500 rendered ⚡7,375. SM-460 retires that: "one displayed
// whole RED, GREEN or GOLD unit represents $1", and phase 03a's own
// pricing_policy seed says "SPARK display retired". 03a had already relabelled
// PRICES through formatPriceDollars, which left wire 5000 rendering "$0.50" as
// a price and "⚡50.00" as an amount on the same screen. Changing the divisor
// below is what closes that.
//
// NOTHING ON THE WIRE MOVED (G2). $1.00 is wire 10000 today exactly as it was
// yesterday, in all three currencies. Only the number of digits left of the
// decimal point changed — a relabel, not a rescale.
//
// The exported names still say SPARK. They are the legacy spelling and get
// renamed in the call-site sweep; renaming them here would have made a
// one-constant change into a 750-site one, and the two are worth separating so
// this diff can be read.
//
// Wire note: POST bodies still send the ledger's `currency: "CREDIT"` literal
// and amounts as minor-unit strings — the display unit is a *display* unit,
// not a wire change. parseSparkToMinor converts a typed amount back to the
// minor-unit string the API expects, and moves with the divisor so a form that
// formats and parses through this module still round-trips exactly.

import { intlLocale } from "@/i18n";

export const SPARK_MINOR_PRECISION = 4; // int64 minor-unit precision on the wire
export const SPARK_DISPLAY_DP = 2; // decimals shown to operators
// REMOVED with SM-491: `SPARK_LABEL`, `formatSparkText` and `currencyLabel`.
//
// All three answered "what is this money called?" with the literal string
// "GOLD", which is a currency's NAME — operator configuration set on
// /currencies, not a constant. A console that keeps its own copy of the name
// keeps showing the old one after a rename, and shows GOLD's name beside a RED
// or GREEN figure. The name now comes from the presentation contract, the same
// place the mark and the precision come from:
//
//   formatSparkText(minor)  →  formatCurrencyText(presentation, minor)   @/currency
//   currencyLabel(code)     →  useCurrencyName(code)                     the provider
//
// This module keeps VALUE only — digits, grouping, truncation and the entry
// unit. It has no opinion about identity, which is what makes it impossible
// for a hardcoded currency word to come back through it.

// One displayed unit is one dollar, and the wire is a dollar-major value at
// precision 4 — so the divisor IS 10^PRECISION. e.g. wire 737500 → 73.75.
const WIRE_PER_UNIT = 10 ** SPARK_MINOR_PRECISION; // 10 000

/**
 * Wire minor units per ONE displayed unit. The same 10 000 for all three
 * currencies: the SM-443 presentation contract carries a per-currency
 * `unitMinor` and passes it through FormatSparkOptions, but since SM-460 every
 * currency's value is this one.
 *
 * This is a DISPLAY divisor and nothing else (G2). The wire integer is
 * identical for all three currencies — $1.00 is 10000 in RED, GREEN and GOLD
 * alike — so changing it changes what a number is CALLED, never what it is
 * worth, and no value here is ever rescaled before it is formatted.
 */
export const SPARK_UNIT_MINOR = WIRE_PER_UNIT;

/**
 * Wire digits per displayed unit, i.e. log10(unitMinor).
 *
 * Only exact powers of ten are accepted; anything else falls back to the $1
 * divisor. A non-power-of-ten would mean the decimal point does not sit on a
 * digit boundary, and the whole point of the integer path below is that the
 * split is a string slice rather than a division — see the float-drift note.
 * A zero or negative divisor would render Infinity.
 */
function unitShift(unitMinor: number | undefined): number {
  if (unitMinor === undefined) return SPARK_MINOR_PRECISION;
  if (!Number.isFinite(unitMinor) || unitMinor < 1) return SPARK_MINOR_PRECISION;
  const shift = Math.round(Math.log10(unitMinor));
  if (shift < 0 || 10 ** shift !== unitMinor) return SPARK_MINOR_PRECISION;
  return shift;
}

function toMajor(minor: string | number | undefined | null, unitMinor: number): number | null {
  if (minor === undefined || minor === null || minor === "") return null;
  const n = typeof minor === "number" ? minor : Number(minor);
  if (!Number.isFinite(n)) return null;
  return n / unitMinor;
}

export interface FormatSparkOptions {
  /** Decimal places (default 2). */
  dp?: number;
  /**
   * Wire minor units per displayed unit (default SPARK_UNIT_MINOR = 10 000).
   * The SM-443 contract passes each currency's `unitMinor`; since SM-460 all
   * three carry this same value, so the option exists to keep the contract
   * explicit rather than to express a difference.
   *
   * It moves the DECIMAL POINT, it does not rescale the amount. The value
   * handed to this function is always the original wire integer, so no digit
   * is lost at any dp — which is precisely what a `n / (unitMinor / 100)`
   * pre-division would do, since that produces a float and the integer path
   * below then truncates it (SM-484).
   */
  unitMinor?: number;
  /** Force a leading "+" on positive values (P&L deltas). */
  sign?: boolean;
  /** Drop a trailing ".00" for whole amounts. */
  trimZeros?: boolean;
  /** Rendered when the input is nullish/NaN (default "—"). */
  placeholder?: string;
}

/**
 * Format int64 minor units as a grouped number string (no unit symbol),
 * truncated to `dp`. Negative values get a real minus (U+2212).
 *   formatSpark("5900")          → "0.59"      (a $0.59 price)
 *   formatSpark("737500")        → "73.75"     (a balance)
 *   formatSpark("-5000")         → "−0.50"
 */
export function formatSpark(
  minor: string | number | undefined | null,
  opts: FormatSparkOptions = {},
): string {
  const {
    dp = SPARK_DISPLAY_DP,
    sign = false,
    trimZeros = false,
    placeholder = "—",
    unitMinor,
  } = opts;
  const shift = unitShift(unitMinor);
  const major = toMajor(minor, 10 ** shift);
  if (major === null) return placeholder;
  const digits = truncatedDigits(minor as string | number, dp, trimZeros, shift);
  if (digits === null) return placeholder;
  const body = groupDigits(digits.whole) + (digits.fraction ? decimalSeparator() + digits.fraction : "");
  if (major < 0) return "−" + body;
  if (sign && major > 0) return "+" + body;
  return body;
}

// ── Why the digits are computed in integers, not floats ──────────────
//
// This used to be `Math.trunc(major * 10 ** dp) / 10 ** dp`, and it was
// wrong for roughly one wire value in fifteen. `1999 / 100` is
// 19.990000000000002 in binary floating point; multiplying back by 100
// gives 1998.9999999999998, and truncating that lands on 1998 — so a
// 19.99 balance rendered as 19.98. Same for wire 29 → 0.28, 57 →
// 0.56, 113 → 1.12, 123456789 → 1,234,567.88. Always one unit SHORT,
// never over, which is why it survived so long: it reads as
// conservative rounding rather than as a bug.
//
// Truncation is the deliberate rule here (an operator must never see
// more than a wallet can spend), so it has to be exact. The wire value
// is an integer to begin with, so the whole calculation stays in
// integer space and the only float that survives is the sign test.
// Found and fixed in web-client during 06a; ported here byte-for-byte
// so the three apps' digits cannot drift (SM-443 parity fixture).

interface TruncatedDigits {
  /** Absolute integer part, ungrouped: "1234567". */
  whole: string;
  /** Fraction digits without a separator, already trimmed: "89" or "". */
  fraction: string;
}

/**
 * Split |minor| into display digits at `dp` decimals, truncating toward
 * zero. `minor` is a wire integer at SPARK_MINOR_PRECISION; the displayed
 * value is minor / WIRE_PER_UNIT, so displaying `dp` decimals keeps
 * the top `dp` digits of that division and drops the rest.
 */
function truncatedDigits(
  minor: string | number,
  dp: number,
  trimZeros: boolean,
  shift: number,
): TruncatedDigits | null {
  const raw = typeof minor === "number" ? String(Math.trunc(minor)) : String(minor).trim();
  const negative = raw.startsWith("-");
  const abs = (negative ? raw.slice(1) : raw).replace(/^\+/, "");
  // Non-integer input (a float that slipped through, scientific
  // notation) — fall back to the float path rather than mangling it.
  if (!/^\d+$/.test(abs)) {
    const n = Math.abs(Number(minor));
    if (!Number.isFinite(n)) return null;
    const f = 10 ** dp;
    const t = Math.trunc((n / 10 ** shift) * f) / f;
    const [w, fr = ""] = t.toFixed(dp).split(".");
    return { whole: w, fraction: trimZeros ? fr.replace(/0+$/, "") : fr };
  }
  // Digits of |minor| shifted so the decimal point sits `shift` digits
  // from the right — 4 for the $1 currency unit all three currencies use
  // (1 unit = 10 000 wire). Pad so there is always something on the right
  // of it.
  const padded = abs.padStart(shift + 1, "0");
  const whole = padded.slice(0, padded.length - shift).replace(/^0+(?=\d)/, "");
  const available = padded.slice(padded.length - shift);
  // Keep dp digits, padding with zeros when the wire carries fewer.
  const fractionAll = (available + "0".repeat(Math.max(0, dp - available.length))).slice(0, dp);
  return { whole, fraction: trimZeros ? fractionAll.replace(/0+$/, "") : fractionAll };
}

/** Group an ungrouped integer-digit string in the operator's language. */
function groupDigits(whole: string): string {
  // Values beyond Number.MAX_SAFE_INTEGER cannot round-trip through
  // Number, and a treasury balance is allowed to be one. Group by hand
  // in that case using the locale's own separator.
  const n = Number(whole);
  if (Number.isSafeInteger(n)) {
    try {
      return n.toLocaleString(intlLocale(), { useGrouping: true, maximumFractionDigits: 0 });
    } catch {
      return n.toLocaleString(undefined, { useGrouping: true, maximumFractionDigits: 0 });
    }
  }
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, separatorsOf().group);
}

/** The active locale's decimal separator ("." / "," / "٫"). */
function decimalSeparator(): string {
  return separatorsOf().decimal;
}

function separatorsOf(): { decimal: string; group: string } {
  let locale: string | undefined;
  try {
    locale = intlLocale();
  } catch {
    locale = undefined;
  }
  try {
    const parts = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      useGrouping: true,
    }).formatToParts(1234.5);
    return {
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
      group: parts.find((p) => p.type === "group")?.value ?? ",",
    };
  } catch {
    return { decimal: ".", group: "," };
  }
}

/**
 * Inverse of formatSpark for outbound POSTs: a typed amount → wire minor units.
 * One typed whole unit is one dollar, so "59" → "590000" and "0.50" → "5000".
 *   parseSparkToMinor("59")     → "590000"
 *   parseSparkToMinor("0.50")   → "5000"
 * Commas are tolerated (formatted input). Returns "0" for empty/NaN.
 *
 * **This moved with the divisor.** It used to shift by 10^(precision−2) because
 * a typed unit was one cent; it now shifts by 10^precision because a typed unit
 * is one dollar. Display and entry move together on purpose — a form whose
 * formatter and parser disagree turns "500" into "50,000" on a round trip, and
 * money forms round-trip constantly (open, edit one field, save).
 *
 * The shift is a STRING slice, not a multiplication. `19.99 * 100` is
 * 1998.9999999999998 in binary floating point, which is the same float drift
 * that made ⚡19.99 render as ⚡19.98 before 06a; the old implementation only
 * escaped it by rounding. Shifting the digits directly lets this truncate —
 * the rule the rest of this module follows — without rounding a typed amount
 * up into money the operator did not intend to move.
 */
export function parseSparkToMinor(
  input: string | number | undefined | null,
  precision = SPARK_MINOR_PRECISION,
): string {
  if (input === undefined || input === null || input === "") return "0";
  const raw = String(input).replace(/,/g, "").trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!m) {
    // Scientific notation or junk. Fall back to the float path rather than
    // mangling it; a non-finite value reads as zero.
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 10 ** precision).toString() : "0";
  }
  const [, sgn, whole = "", frac = ""] = m;
  if (whole === "" && frac === "") return "0";
  // Digits of the typed amount with the point moved `precision` places right,
  // padding when fewer were typed and TRUNCATING when more were.
  const shifted = (whole + frac.padEnd(precision, "0").slice(0, precision)).replace(/^0+(?=\d)/, "");
  if (shifted === "" || /^0+$/.test(shifted)) return "0";
  return (sgn === "-" ? "-" : "") + shifted;
}

